import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { useAuth } from '../contexts/AuthContext';
import { AuthBrandPanel, BrandMark } from '../components/AuthBrandPanel';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '../components/ui/form';
import { Alert, AlertDescription } from '../components/ui/alert';
import { config } from '../config';

const loginSchema = z.object({
  username: z.string().min(1, 'Username is required'),
  password: z.string().min(1, 'Password is required'),
  organizationCode: z.string().optional(),
});

const passwordSetupSchema = z.object({
  password: z.string().min(8, 'Password must be at least 8 characters'),
  confirmPassword: z.string().min(1, 'Please confirm your password'),
}).refine((data) => data.password === data.confirmPassword, {
  message: "Passwords don't match",
  path: ["confirmPassword"],
});

type LoginFormData = z.infer<typeof loginSchema>;
type PasswordSetupFormData = z.infer<typeof passwordSetupSchema>;

interface OrganizationBranding {
  displayName: string;
  logoUrl: string | null;
  organizationCode: string;
}

const UniversalAuthPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [token, setToken] = useState<string | null>(null);
  const [userEmail, setUserEmail] = useState<string>('');
  const [showOrganizationOverride, setShowOrganizationOverride] = useState(false);
  const [orgBranding, setOrgBranding] = useState<OrganizationBranding>({
    displayName: 'Good Outcomes',
    logoUrl: null,
    organizationCode: ''
  });
  const [isVerifyingToken, setIsVerifyingToken] = useState(false);
  const { login, error: authError } = useAuth();

  const getPostAuthPath = (): string => {
    const returnTo = searchParams.get('return_to');
    if (returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//')) {
      return returnTo;
    }
    return '/v4/today';
  };

  const loginForm = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      username: '',
      password: '',
      organizationCode: '',
    },
  });

  const passwordSetupForm = useForm<PasswordSetupFormData>({
    resolver: zodResolver(passwordSetupSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  // Check for token in URL on mount
  useEffect(() => {
    const tokenParam = searchParams.get('token');
    if (tokenParam) {
      setToken(tokenParam);
      verifyToken(tokenParam);
    }
  }, [searchParams]);

  const verifyToken = async (tokenValue: string) => {
    setIsVerifyingToken(true);
    setError('');
    try {
      const response = await fetch(`${config.API_URL}/v1/auth/magic-link/verify?token=${encodeURIComponent(tokenValue)}`);
      if (response.ok) {
        const data = await response.json();
        setUserEmail(data.email);

        // Set organization branding from API response
        setOrgBranding({
          displayName: data.branding?.display_name || data.organization_name,
          logoUrl: data.branding?.logo_url,
          organizationCode: data.organization_code
        });
      } else {
        const errorData = await response.json();
        setError(errorData.detail || 'Invalid or expired token');
        setToken(null);
        // Remove token from URL
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('token');
        setSearchParams(newParams, { replace: true });
      }
    } catch (err) {
      setError('Failed to verify token. Please try again.');
      setToken(null);
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('token');
      setSearchParams(newParams, { replace: true });
    } finally {
      setIsVerifyingToken(false);
    }
  };

  const handleLogin = async (data: LoginFormData) => {
    setIsLoading(true);
    setError('');

    try {
      const organizationCode = data.organizationCode?.trim() || orgBranding.organizationCode || '';
      const success = await login(data.username, data.password, organizationCode);

      if (success) {
        window.location.href = getPostAuthPath();
      } else {
        setError(authError || 'Login failed. Please check your credentials.');
      }
    } catch (err) {
      setError('An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSetPassword = async (data: PasswordSetupFormData) => {
    if (!token || !orgBranding.organizationCode) return;

    setIsLoading(true);
    setError('');

    try {
      const response = await fetch(`${config.API_URL}/v1/auth/magic-link/set-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: token,
          password: data.password,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to set password');
      }

      const tokenData = await response.json();

      // Store auth data
      const userData = {
        user_id: tokenData.user_id,
        username: tokenData.username,
        email: userEmail,
        organization_id: tokenData.organization_id,
        organization_code: tokenData.organization_code,
        role: tokenData.role,
      };

      // Use the login function from AuthContext to store the token
      localStorage.setItem('token', tokenData.access_token);
      localStorage.setItem('user', JSON.stringify(userData));
      localStorage.setItem('isAuthenticated', 'true');

      window.location.href = getPostAuthPath();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unexpected error occurred. Please try again.');
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  const switchToLogin = () => {
    setToken(null);
    setUserEmail('');
    setShowOrganizationOverride(false);
    setOrgBranding({
      displayName: 'Good Outcomes',
      logoUrl: null,
      organizationCode: ''
    });
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('token');
    setSearchParams(newParams, { replace: true });
  };

  return (
    <div className="min-h-screen flex">
      <AuthBrandPanel />

      {/* Right Column - Login Form */}
      <div className="flex-1 flex flex-col justify-center items-center px-4 py-12 bg-white">
        <div className="w-full max-w-md">
          <Card className="shadow-lg border-0">
            <CardHeader className="text-center space-y-2">
              {/* GO Logo */}
              <div className="mx-auto mb-4">
                <Link to="/">
                  <BrandMark />
                </Link>
              </div>
              {/* Display organization name dynamically */}
              <CardTitle className="text-2xl font-bold">{orgBranding.displayName}</CardTitle>

            </CardHeader>

            <CardContent>
              {error && (
                <Alert variant="destructive" className="mb-6">
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}

              {isVerifyingToken ? (
                <div className="text-center py-8">
                  <p className="text-sm text-muted-foreground">Verifying magic link...</p>
                </div>
              ) : token && userEmail && orgBranding.organizationCode ? (
                // Password setup form
                <Form {...passwordSetupForm}>
                  <form onSubmit={passwordSetupForm.handleSubmit(handleSetPassword)} className="space-y-6">
                    <div>
                      <label className="text-sm font-medium text-gray-700 mb-1 block">Email</label>
                      <Input
                        value={userEmail}
                        disabled
                        className="bg-gray-50"
                      />
                      <p className="text-xs text-muted-foreground mt-1">
                        You cannot change your email here. <button type="button" onClick={switchToLogin} className="text-blue-600 hover:underline">Switch to login</button>
                      </p>
                    </div>

                    <FormField
                      control={passwordSetupForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Enter your password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={passwordSetupForm.control}
                      name="confirmPassword"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Confirm Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Confirm your password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading}
                      size="lg"
                    >
                      {isLoading ? 'Setting Password...' : 'Set Password & Sign In'}
                    </Button>
                  </form>
                </Form>
              ) : (
                // Normal login form
                <Form {...loginForm}>
                  <form onSubmit={loginForm.handleSubmit(handleLogin)} className="space-y-6">
                    <FormField
                      control={loginForm.control}
                      name="username"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Email</FormLabel>
                          <FormControl>
                            <Input
                              placeholder="your.email@example.com"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <FormField
                      control={loginForm.control}
                      name="password"
                      render={({ field }) => (
                        <FormItem>
                          <FormLabel>Password</FormLabel>
                          <FormControl>
                            <Input
                              type="password"
                              placeholder="Enter your password"
                              {...field}
                            />
                          </FormControl>
                          <FormMessage />
                        </FormItem>
                      )}
                    />

                    <div className="space-y-3">
                      <button
                        type="button"
                        className="text-sm text-muted-foreground transition-colors hover:text-foreground"
                        onClick={() => setShowOrganizationOverride((current) => !current)}
                      >
                        {showOrganizationOverride
                          ? 'Hide organization override'
                          : 'Signing into a non-default organization?'}
                      </button>

                      {showOrganizationOverride && (
                        <FormField
                          control={loginForm.control}
                          name="organizationCode"
                          render={({ field }) => (
                            <FormItem>
                              <FormLabel>Organization code</FormLabel>
                              <FormControl>
                                <Input
                                  placeholder="Enter organization code"
                                  {...field}
                                />
                              </FormControl>
                              <p className="text-xs text-muted-foreground">
                                Leave this blank to use your default organization.
                              </p>
                              <FormMessage />
                            </FormItem>
                          )}
                        />
                      )}
                    </div>

                    <Button
                      type="submit"
                      className="w-full"
                      disabled={isLoading}
                      size="lg"
                    >
                      {isLoading ? 'Signing In...' : 'Sign In'}
                    </Button>
                  </form>
                </Form>
              )}

              <div className="mt-6 text-center">
                <div className="flex justify-center space-x-4 text-sm">
                  <Link
                    to="/auth/reset-password"
                    className="text-primary hover:text-primary/80 transition-colors"
                  >
                    Forgot Password?
                  </Link>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default UniversalAuthPage;
